import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';

interface FAQItem {
  question: string;
  answer: string;
  open: boolean;
}

@Component({
  selector: 'app-seo-landingpage',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './seo-landingpage.component.html',
  styleUrls: ['./seo-landingpage.component.scss']
})
export class SeoLandingpageComponent implements OnInit {
  faqs: FAQItem[] = [
    {
      question: 'Wie viel Vorlaufzeit wird für die Buchung benötigt?',
      answer: 'Wir empfehlen eine Anfrage mindestens 2 Wochen vor dem geplanten Einsatzdatum. Bei kurzfristigen Anfragen kontaktieren Sie uns direkt – oftmals können wir auch spontane Buchungen realisieren. Verfügbarkeit wird sofort geprüft.',
      open: false
    },
    {
      question: 'Ist die Mobile Videowand wetterfest?',
      answer: 'Ja, unsere Videowand sind speziell für Outdoor-Einsätze konzipiert und vollständig wetterfest. Sie können bei Sonnenschein, Regen, Wind und anderen Wetterbedingungen zuverlässig eingesetzt werden. Die Helligkeit (4.500 Nits) sorgt auch bei direkter Sonneneinstrahlung für ausgezeichnete Sichtbarkeit.',
      open: false
    },
    {
      question: 'Welche Content-Formate und Zuspielsysteme werden unterstützt?',
      answer: 'Wir unterstützen Videos, Bilder, Präsentationen, Live-Streams und dynamische Inhalte in allen gängigen Formaten. Zugleich können Sie über HDMI direkt Inhalte zuspielen oder unsere Media-Player mit vorab bereitgestellten Inhalten nutzen. Gerne beraten wir Sie zur optimalen Aufbereitung.',
      open: false
    },
    {
      question: 'Ist Lieferung und Transport im Mietpreis enthalten?',
      answer: 'Lieferung und Transport werden separat berechnet und richten sich nach Ihrer Entfernung von Leipzig. Im Raum Leipzig (bis ca. 20 km) berechnen wir eine faire Pauschale. Bei der Anfrage erhalten Sie sofort eine transparent Gesamtkalkulation mit allen Kosten – keine versteckten Gebühren.',
      open: false
    },
    {
      question: 'Können Sie beim Aufbau und bei der Einweisung unterstützen?',
      answer: 'Selbstverständlich! Unser Team übernimmt die komplette Lieferung, Aufstellung und Inbetriebnahme. Optional bieten wir auch technische Betreuung während Ihrer Veranstaltung an. Sie müssen sich um nichts kümmern – wir machen es möglich.',
      open: false
    },
    {
      question: 'Welche Rabatte gibt es bei längeren Mietzeiten?',
      answer: 'Bei Langzeitmieten ab 7 Tagen bieten wir attraktive Staffelpreise (ca. 343 €/Tag), ab 14 Tagen sogar noch günstiger (ca. 270 €/Tag). Sprechen Sie uns für ein individuelles Angebot an – je länger, desto besser der Preis.',
      open: false
    }
  ];

  useCases = [
    {
      title: 'Stadtfeste & Märkte',
      description: 'Live-Übertragungen, Programmhinweise und Sponsorenpräsentation für Festivals und Stadtereignisse'
    },
    {
      title: 'Firmenveranstaltungen',
      description: 'Präsentationen, Produktlaunches und Tagungen mit professioneller Bildqualität'
    },
    {
      title: 'Messen & Kongresse',
      description: 'Messeauftritte und Ausstellungen mit hochwertiger Darstellung'
    },
    {
      title: 'Sportevents',
      description: 'Live-Ergebnisse, Replays und Sponsorenwerbung bei Sportveranstaltungen'
    },
    {
      title: 'Baustellenkommunikation',
      description: 'Projektvisualisierung und Informationen für Anwohner und Passanten'
    },
    {
      title: 'Konzerte & Shows',
      description: 'Bühnenbegleitung, Live-Übertragungen und atmosphärische Visuals'
    }
  ];

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.setPageMetadata();
    this.injectStructuredData();
  }

  private setPageMetadata(): void {
    // Title Tag
    this.titleService.setTitle(
      'Mobile Videowand in Leipzig mieten | LED Videowand Outdoor Events'
    );

    // Meta Tags
    this.metaService.updateTag({
      name: 'description',
      content:
        'Mobile Videowand in Leipzig mieten: hochauflösend 2,6mm Pixelpitch, outdoor-tauglich, 2,5×1,5m, 4.500 Nits Helligkeit. Perfekt für Events, Messen, Stadtfeste. Ab 490€/Tag.'
    });

    this.metaService.updateTag({
      name: 'keywords',
      content:
        'mobile Videowand leipzig, led videowand mieten, outdoor Videowand, videodisplay events, led display messe'
    });

    // Canonical URL
    this.metaService.updateTag({
      rel: 'canonical',
      href: 'https://videowand-leipzig.de/mobile-videowall-leipzig-mieten'
    });

    // Open Graph Tags
    this.metaService.updateTag({
      property: 'og:title',
      content: 'Mobile Videowand in Leipzig mieten – Outdoor LED-Videowand für Events'
    });

    this.metaService.updateTag({
      property: 'og:description',
      content:
        'Hochauflösend, wetterfest, mobil: Mieten Sie unsere professionelle Mobile Videowand für Ihre Events in Leipzig und Umgebung.'
    });

    this.metaService.updateTag({
      property: 'og:type',
      content: 'website'
    });

    this.metaService.updateTag({
      property: 'og:url',
      content: 'https://videowand-leipzig.de/mobile-videowall-leipzig-mieten'
    });

    this.metaService.updateTag({
      property: 'og:image',
      content: 'https://videowand-leipzig.de/assets/og-image-videowall.jpg'
    });

    this.metaService.updateTag({
      property: 'og:image:width',
      content: '1200'
    });

    this.metaService.updateTag({
      property: 'og:image:height',
      content: '630'
    });

    // Twitter Tags
    this.metaService.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image'
    });

    this.metaService.updateTag({
      name: 'twitter:title',
      content: 'Mobile Videowand in Leipzig mieten'
    });

    this.metaService.updateTag({
      name: 'twitter:description',
      content:
        'Professionelle LED-Videowand für Events in Leipzig – Outdoor, hochauflösend, ab 490€/Tag'
    });

    this.metaService.updateTag({
      name: 'twitter:image',
      content: 'https://videowand-leipzig.de/assets/og-image-videowall.jpg'
    });

    // Lokalisierung
    this.metaService.updateTag({
      name: 'language',
      content: 'de-DE'
    });

    this.metaService.updateTag({
      name: 'geo.region',
      content: 'DE-SN'
    });

    this.metaService.updateTag({
      name: 'geo.placename',
      content: 'Leipzig'
    });

    this.metaService.updateTag({
      name: 'geo.position',
      content: '51.3397;12.3731'
    });
  }

  private injectStructuredData(): void {
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify(this.getStructuredDataSchema());
    document.head.appendChild(schemaScript);
  }

  private getStructuredDataSchema(): object {
    return {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': 'https://videowand-leipzig.de/',
      name: 'Videowand Leipzig',
      image: 'https://videowand-leipzig.de/assets/logo.svg',
      description: 'Mobile Videowand für Events und Veranstaltungen in Leipzig',
      url: 'https://videowand-leipzig.de',
      telephone: '+49 341 1234567',
      email: 'info@videowand-leipzig.de',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Beispielstraße 123',
        addressLocality: 'Leipzig',
        addressRegion: 'Sachsen',
        postalCode: '04103',
        addressCountry: 'DE'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 51.3397,
        longitude: 12.3731
      },
      areaServed: {
        '@type': 'City',
        name: 'Leipzig'
      },
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'EUR',
        lowPrice: '270',
        highPrice: '490',
        offerCount: 3,
        offers: [
          {
            '@type': 'Offer',
            name: 'Tagesmiete Mobile Videowand',
            description: 'Mobile Videowand Tagesmiete (2,6mm Pixelpitch)',
            price: '490',
            priceCurrency: 'EUR',
            availability: 'https://schema.org/InStock'
          },
          {
            '@type': 'Offer',
            name: 'Wochenmiete',
            description: 'Mobile Videowand Wochenmiete (7 Tage)',
            price: '343',
            priceCurrency: 'EUR',
            availability: 'https://schema.org/InStock'
          },
          {
            '@type': 'Offer',
            name: 'Langzeitmiete',
            description: 'Mobile Videowand Langzeitmiete (14 Tage)',
            price: '270',
            priceCurrency: 'EUR',
            availability: 'https://schema.org/InStock'
          }
        ]
      },
      priceRange: '€€',
      sameAs: [
        'https://www.facebook.com/videowand-leipzig',
        'https://www.instagram.com/videowand.leipzig'
      ]
    };
  }

  toggleFAQ(index: number): void {
    this.faqs[index].open = !this.faqs[index].open;
  }

  navigateToBooking(): void {
    this.router.navigate(['/booking']);
  }

  scrollToNextSection(): void {
    console.log('scrollToNextSection called');
    const nextSection = document.getElementById('was-sie-mieten');
    console.log('Found section:', nextSection);
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      console.error('Section with id "was-sie-mieten" not found');
    }
  }
}
